import { useMemo, useCallback } from 'react';
import { 
  FinancialData, 
  FinancialChartConfig, 
  UseFinancialChartReturn, 
  ChartClickEvent,
  ChartData,
  ChartOptions,
} from '../types/chartTypes';
import { 
  CHART_COLORS,
  CHART_HOVER_COLORS,
  FINANCIAL_CHART_OPTIONS,
  ACCOUNT_LABELS 
} from '../constants/chartConstants';

interface UseFinancialChartProps {
  data: FinancialData[];
  period: 'monthly' | 'yearly';
  accountType: 'general' | 'reserve' | 'both';
  showBalanceLine: boolean;
  showCumulativeBalance?: boolean;
  onChartClick?: (event: ChartClickEvent) => void;
  currentFiscalYear?: number | null;
}

export const useFinancialChart = ({
  data,
  period,
  accountType,
  showBalanceLine,
  showCumulativeBalance = false,
  onChartClick,
  currentFiscalYear,
}: UseFinancialChartProps): UseFinancialChartReturn => {
  
  // 累積残高計算関数
  const calculateCumulativeBalance = useCallback((
    financialData: FinancialData[], 
    accType: 'general' | 'reserve' | 'both'
  ): number[] => {
    let cumulativeBalance = 0;
    return financialData.map(d => {
      const netIncome = accType === 'both' 
        ? (d.generalIncome + d.reserveIncome) - (d.generalExpense + d.reserveExpense)
        : accType === 'general'
        ? d.generalIncome - d.generalExpense
        : d.reserveIncome - d.reserveExpense;
      
      cumulativeBalance += netIncome;
      return cumulativeBalance;
    });
  }, []);
  
  // チャートデータの生成
  const chartData = useMemo((): ChartData => {
    if (!data || data.length === 0) {
      return { labels: [], datasets: [] };
    }

    const isMonthly = period === 'monthly';
    const labels = isMonthly 
      ? data.map(d => {
          const monthStr = d.month?.toString() || '';
          return currentFiscalYear ? monthStr.replace(`${currentFiscalYear}年`, '') : monthStr;
        })
      : data.map(d => d.year?.toString() || '');

    const datasets = [];

    // 両方選択時の積み上げ棒グラフ
    if (accountType === 'both') {
      // 収入の積み上げ（正の値）
      datasets.push({
        type: 'bar' as const,
        label: '一般会計収入',
        data: data.map(d => d.generalIncome),
        backgroundColor: CHART_COLORS.GENERAL_INCOME,
        borderColor: CHART_HOVER_COLORS.GENERAL_INCOME,
        borderWidth: 1,
        stack: 'income',
        order: 2,
      });

      datasets.push({
        type: 'bar' as const,
        label: '積立会計収入',
        data: data.map(d => d.reserveIncome),
        backgroundColor: CHART_COLORS.RESERVE_INCOME,
        borderColor: CHART_HOVER_COLORS.RESERVE_INCOME,
        borderWidth: 1,
        stack: 'income',
        order: 2,
      });

      // 支出の積み上げ（負の値）
      datasets.push({
        type: 'bar' as const,
        label: '一般会計支出',
        data: data.map(d => -d.generalExpense),
        backgroundColor: CHART_COLORS.GENERAL_EXPENSE,
        borderColor: CHART_HOVER_COLORS.GENERAL_EXPENSE,
        borderWidth: 1,
        stack: 'expense',
        order: 2,
      });

      datasets.push({
        type: 'bar' as const,
        label: '積立会計支出',
        data: data.map(d => -d.reserveExpense),
        backgroundColor: CHART_COLORS.RESERVE_EXPENSE,
        borderColor: CHART_HOVER_COLORS.RESERVE_EXPENSE,
        borderWidth: 1,
        stack: 'expense',
        order: 2,
      });

      // 収支線の追加
      if (showBalanceLine) {
        const balanceLabel = accountType === 'both' ? '収支' :
          accountType === 'general' ? '一般会計収支' : '積立会計収支';
        const balanceData = accountType === 'both' 
          ? data.map(d => (d.generalIncome + d.reserveIncome) - (d.generalExpense + d.reserveExpense))
          : accountType === 'general'
          ? data.map(d => d.generalIncome - d.generalExpense)
          : data.map(d => d.reserveIncome - d.reserveExpense);
        
        datasets.push({
          type: 'line' as const,
          label: balanceLabel,
          data: balanceData,
          borderColor: CHART_COLORS.BALANCE_LINE,
          backgroundColor: 'rgba(31, 119, 180, 0.3)',
          borderWidth: 3,
          fill: false,
          tension: 0,
          borderDash: [5, 5],
          pointRadius: 5,
          pointHoverRadius: 10,
          pointBackgroundColor: CHART_COLORS.BALANCE_LINE,
          pointBorderColor: '#fff',
          pointBorderWidth: 1,
          order: 0,
          yAxisID: 'y',
          stack: 'balance',
        });
      }
    } else {
      // 一般会計のみ
      if (accountType === 'general') {
        datasets.push({
          type: 'bar' as const,
          label: '一般会計収入',
          data: data.map(d => d.generalIncome),
          backgroundColor: CHART_COLORS.GENERAL_INCOME,
          borderColor: CHART_HOVER_COLORS.GENERAL_INCOME,
          borderWidth: 1,
          stack: 'income',
          order: 2,
        });

        datasets.push({
          type: 'bar' as const,
          label: '一般会計支出',
          data: data.map(d => -d.generalExpense),
          backgroundColor: CHART_COLORS.GENERAL_EXPENSE,
          borderColor: CHART_HOVER_COLORS.GENERAL_EXPENSE,
          borderWidth: 1,
          stack: 'expense',
          order: 2,
        });

        // 一般会計の収支線
        if (showBalanceLine) {
          datasets.push({
            type: 'line' as const,
            label: '一般会計収支',
            data: data.map(d => d.generalIncome - d.generalExpense),
            borderColor: CHART_COLORS.BALANCE_LINE,
            backgroundColor: 'rgba(31, 119, 180, 0.3)',
            borderWidth: 3,
            fill: false,
            tension: 0,
            borderDash: [5, 5],
            pointRadius: 5,
            pointHoverRadius: 10,
            pointBackgroundColor: CHART_COLORS.BALANCE_LINE,
            pointBorderColor: '#fff',
            pointBorderWidth: 1,
            order: 1,
            yAxisID: 'y',
          });
        }
      }
      // 積立会計のみ
      if (accountType === 'reserve') {
        datasets.push({
          type: 'bar' as const,
          label: '積立会計収入',
          data: data.map(d => d.reserveIncome),
          backgroundColor: CHART_COLORS.RESERVE_INCOME,
          borderColor: CHART_HOVER_COLORS.RESERVE_INCOME,
          borderWidth: 1,
          stack: 'income',
          order: 2,
        });

        datasets.push({
          type: 'bar' as const,
          label: '積立会計支出',
          data: data.map(d => -d.reserveExpense),
          backgroundColor: CHART_COLORS.RESERVE_EXPENSE,
          borderColor: CHART_HOVER_COLORS.RESERVE_EXPENSE,
          borderWidth: 1,
          stack: 'expense',
          order: 2,
        });

        // 積立会計の収支線
        if (showBalanceLine) {
          datasets.push({
            type: 'line' as const,
            label: '積立会計収支',
            data: data.map(d => d.reserveIncome - d.reserveExpense),
            borderColor: CHART_COLORS.BALANCE_LINE,
            backgroundColor: 'rgba(31, 119, 180, 0.3)',
            borderWidth: 3,
            fill: false,
            tension: 0,
            borderDash: [5, 5],
            pointRadius: 5,
            pointHoverRadius: 10,
            pointBackgroundColor: CHART_COLORS.BALANCE_LINE,
            pointBorderColor: '#fff',
            pointBorderWidth: 1,
            order: 1,
            yAxisID: 'y',
          });
        }
      }
    }

    // 累積残高線の追加
    if (showCumulativeBalance && period === 'yearly') {
      const cumulativeData = calculateCumulativeBalance(data, accountType);
      datasets.push({
        type: 'line' as const,
        label: '残高',
        data: cumulativeData,
        borderColor: CHART_COLORS.CUMULATIVE_BALANCE,
        backgroundColor: 'rgba(44, 62, 130, 0.3)',
        borderWidth: 3,
        fill: false,
        tension: 0,
        pointRadius: 5,
        pointHoverRadius: 10,
        pointBackgroundColor: CHART_COLORS.CUMULATIVE_BALANCE,
        pointBorderColor: '#fff',
        pointBorderWidth: 1,
        order: 1,
        yAxisID: 'y',
        stack: 'cumlative',
      });
    }

    return { labels, datasets };
  }, [data, period, accountType, showBalanceLine, showCumulativeBalance, currentFiscalYear, calculateCumulativeBalance]);

  // チャートオプションの生成
  const chartOptions = useMemo((): ChartOptions => {
    const title = (() => {
      const periodLabel = period === 'monthly' ? '月別' : '年別';
      const accountLabel = ACCOUNT_LABELS[accountType];
      return `${periodLabel}収支推移（${accountLabel}）`;
    })();

    return {
      ...FINANCIAL_CHART_OPTIONS,
      plugins: {
        ...FINANCIAL_CHART_OPTIONS.plugins,
        title: {
          display: true,
          text: title,
        },
      },
      onHover: (event: any, elements: any[]) => {
        if (event?.native?.target) {
          event.native.target.style.cursor = elements.length > 0 ? 'pointer' : 'default';
        }
      },
      onClick: (event: any, elements: any[], chart: any) => {
        handleChartClick({ event, elements, chart });
      },
    };
  }, [period, accountType]);

  // チャートクリックハンドラ
  const handleChartClick = useCallback(({ event, elements, chart }: { 
    event: any; 
    elements: any[]; 
    chart: any; 
  }) => {
    if (!onChartClick || elements.length === 0) return;

    try {
      const element = elements[0];
      const datasetIndex = element.datasetIndex;
      const dataIndex = element.index;
      const dataset = chartData.datasets[datasetIndex];
      const label = chartData.labels[dataIndex];
      const value = Math.abs(dataset.data[dataIndex]);

      // データセットラベルから会計種別と取引種別を推定
      const isGeneralAccount = dataset.label?.includes('一般会計') || false;
      const isIncomeTransaction = dataset.label?.includes('収入') || false;

      const clickEvent: ChartClickEvent = {
        datasetIndex,
        dataIndex,
        value,
        label,
        accountType: isGeneralAccount ? 'general' : 'reserve',
        type: isIncomeTransaction ? 'income' : 'expense',
        period: label,
      };

      onChartClick(clickEvent);
    } catch (error) {
      console.error('Chart click handling error:', error);
    }
  }, [onChartClick, chartData]);

  return {
    chartData,
    chartOptions,
    handleChartClick: ({ event, elements, chart }: any) => handleChartClick({ event, elements, chart }),
    isLoading: !data || data.length === 0,
    error: null,
  };
};